import { useCallback } from "react";

import { ClientReport } from "apps/company/api/rest/company-dashboard-reports";
import { useCompanyDashboardAccess } from "apps/company/api/rest/company-dashboard-access";
import { useAuth } from "providers/auth";
import { trackEvent } from "utility/mixpanel";

export type DashboardTab =
  | "engagement"
  | "resilience"
  | "business_impact"
  | "reports";

export type ReportAction = "viewed" | "opened" | "downloaded";

export type DashboardContactLink = "invest" | "email";

export type DashboardFilter =
  | "half"
  | "cohort"
  | "quarter"
  | "version"
  | "participation"
  | "geography"
  | "department"
  | "people";

const WONE_DOMAIN = "@walkingonearth.com";

// Which client's admins use the dashboard, never what it shows them: only the
// company, the tab, which control was used, which report (id, title and
// period) and whether the viewer is WONE staff reach Mixpanel. The company is
// the one the dashboard is for, which can differ from the admin's employer.
export const useDashboardTracking = () => {
  const { user } = useAuth();
  const { company } = useCompanyDashboardAccess();
  const companyId = company?.id ?? null;
  const companyName = company?.name ?? null;
  const isWoneStaff = (user?.email ?? "").toLowerCase().endsWith(WONE_DOMAIN);

  const viewed = useCallback(
    (tab: DashboardTab) =>
      trackEvent("Client Dashboard Viewed", {
        "Company ID": companyId,
        "Company Name": companyName,
        Tab: tab,
        "Is WONE Staff": isWoneStaff,
      }),
    [companyId, companyName, isWoneStaff]
  );

  const filterChanged = useCallback(
    (tab: DashboardTab, filter: DashboardFilter) =>
      trackEvent("Client Dashboard Filter Changed", {
        "Company ID": companyId,
        Filter: filter,
        Tab: tab,
        "Is WONE Staff": isWoneStaff,
      }),
    [companyId, isWoneStaff]
  );

  // Like a filter change, an interaction: the company id, no name.
  const contactClicked = useCallback(
    (tab: DashboardTab, link: DashboardContactLink) =>
      trackEvent("Client Dashboard Contact Clicked", {
        "Company ID": companyId,
        Link: link,
        Tab: tab,
        "Is WONE Staff": isWoneStaff,
      }),
    [companyId, isWoneStaff]
  );

  // Which report was used, by title and period (always in English, whatever
  // the viewer's language, so the figures group); never its contents.
  const reportAccessed = useCallback(
    (action: ReportAction, report: ClientReport) =>
      trackEvent("Client Dashboard Report Accessed", {
        "Company ID": companyId,
        "Company Name": companyName,
        Action: action,
        "Report ID": report.id,
        Period: `Q${report.quarter} ${report.year}`,
        Title: report.title,
        "Is WONE Staff": isWoneStaff,
      }),
    [companyId, companyName, isWoneStaff]
  );

  // A change Platform kept, so the count matches who is emailed.
  const reportEmailsToggled = useCallback(
    (enabled: boolean) =>
      trackEvent("Client Dashboard Report Emails Toggled", {
        "Company ID": companyId,
        "Company Name": companyName,
        Enabled: enabled,
        Tab: "reports",
        "Is WONE Staff": isWoneStaff,
      }),
    [companyId, companyName, isWoneStaff]
  );

  return {
    viewed,
    filterChanged,
    contactClicked,
    reportAccessed,
    reportEmailsToggled,
  };
};
